"use client";

import React, { useState } from "react";
// Locale-aware Link: plain next/link emitted unprefixed hrefs, so every CTA on
// this page cost a 307 to the /{locale} version on the way in.
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import MarketingNavBar from "@/components/MarketingNavBar";
import {
  DISPLAY_VAT_RATE,
  STANDARD_PRICE_MINOR,
  minorToMajor,
} from "@/utils/subscription/pricing";

// The one published price, taken from the same constant the app bills against.
const PRICE = minorToMajor(STANDARD_PRICE_MINOR);

function fmt(n: number): string {
  return Math.round(n).toLocaleString("ro-RO");
}

const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const DollarIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

export default function PricingPage() {
  const t = useTranslations("Pricing");

  const [bookings, setBookings] = useState(320);
  const [avgValue, setAvgValue] = useState(70);
  const [commission, setCommission] = useState(12);

  const mpCost = bookings * avgValue * (commission / 100);
  const savings = Math.max(0, mpCost - PRICE);
  const maxBar = Math.max(mpCost, PRICE, 1);
  const mpBarPct = (mpCost / maxBar) * 100;
  const atBarPct = Math.max(5, (PRICE / maxBar) * 100);

  const cardFeatures = t.raw("Card.CardFeatures") as [string, string][];
  const trustItems = t.raw("Trust") as [string, string][];
  const includedFeatures = t.raw("Included.Features") as [string, string][];
  return (
    <div
      style={{
        background: "#0f1623",
        color: "#fff",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        minHeight: "100vh",
      }}
    >
      <MarketingNavBar />

      {/* ── HERO ── */}
      <section style={{ maxWidth: "80rem", margin: "0 auto", padding: "5rem 1.5rem" }}>
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Copy */}
          <div>
            <p style={{ fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600, color: "#60A5FA", marginBottom: "1.25rem" }}>
              {t("Hero.Kicker")}
            </p>
            <h1 style={{ fontSize: "clamp(2.4rem, 4.6vw, 3.9rem)", fontWeight: 800, lineHeight: 1.04, letterSpacing: "-0.028em", marginBottom: "1.5rem" }}>
              {t("Hero.HeadingPre")} <span style={{ color: "#60A5FA" }}>{t("Hero.HeadingAccent")}</span> {t("Hero.HeadingPost")}
            </h1>
            <p style={{ color: "#d1d5db", fontSize: "1.05rem", lineHeight: 1.7, marginBottom: "2rem", maxWidth: "28rem" }}>
              {t("Hero.Description")}
            </p>
            <div className="flex flex-wrap gap-3 mb-5">
              <Link
                href="/pre-registration"
                className="flex items-center gap-2 px-7 py-3.5 text-[0.95rem] font-bold rounded-xl text-white"
                style={{ background: "#3b82f6", boxShadow: "0 10px 30px -12px rgba(59,130,246,0.6)" }}
              >
                {t("Hero.CtaPrimary")}
                <ArrowRightIcon />
              </Link>
              <a
                href="#include"
                className="flex items-center px-7 py-3.5 text-[0.95rem] font-semibold rounded-xl"
                style={{ color: "#d1d5db", border: "1px solid #374151" }}
              >
                {t("Hero.CtaSecondary")}
              </a>
            </div>
            <p style={{ fontSize: "0.82rem", color: "#4b5563" }}>{t("Hero.FinePrint")}</p>
          </div>

          {/* Pricing card column */}
          <div>
            {/* Price card */}
            <div
              style={{
                position: "relative",
                background: "linear-gradient(180deg,#1f2937,#192231)",
                border: "1px solid rgba(59,130,246,0.35)",
                borderRadius: "1.25rem",
                boxShadow: "0 30px 70px -28px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.04)",
                padding: "2rem",
              }}
            >
              <div style={{ position: "absolute", inset: 0, borderRadius: "inherit", pointerEvents: "none", background: "radial-gradient(120% 60% at 50% 0%, rgba(59,130,246,0.12), transparent 60%)" }} />
              <div style={{ position: "relative" }}>
                <span
                  className="inline-flex items-center gap-1.5 mb-5"
                  style={{ background: "rgba(59,130,246,0.14)", color: "#60A5FA", borderRadius: "999px", fontSize: "0.72rem", fontWeight: 600, padding: "0.28rem 0.7rem" }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: "999px", background: "#60A5FA", display: "inline-block" }} />
                  {t("Card.Badge")}
                </span>

                <div className="flex items-end gap-3 mt-4 mb-2">
                  <div className="flex items-baseline gap-1.5">
                    <span style={{ fontSize: "3.4rem", lineHeight: 1, fontWeight: 800, letterSpacing: "-0.02em" }}>{PRICE}</span>
                    <span style={{ color: "#9ca3af", fontSize: "1.125rem", fontWeight: 600 }}>{t("Card.Currency")}</span>
                  </div>
                  <span style={{ color: "#4b5563", fontSize: "0.875rem", paddingBottom: "0.5rem" }}>{t("Card.PerMonth")}</span>
                </div>

                <p style={{ fontSize: "0.86rem", color: "#9ca3af", lineHeight: 1.6, marginBottom: "0.25rem" }}>
                  {t("Card.PriceSummary")}
                </p>
                <p style={{ fontSize: "0.72rem", color: "#374151", marginBottom: "1.5rem" }}>
                  {t("Card.VatNote", { rate: DISPLAY_VAT_RATE })}
                </p>

                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1.75rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {cardFeatures.map(([title, note]) => (
                    <li key={title} className="flex items-start gap-3" style={{ fontSize: "0.9rem", color: "#e5e7eb" }}>
                      <span style={{ width: 18, height: 18, borderRadius: "999px", background: "rgba(52,211,153,0.12)", color: "#34D399", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                        <CheckIcon />
                      </span>
                      {/* The separator between the two halves belongs to the locale, not here. */}
                      <span><span style={{ color: "#fff", fontWeight: 600 }}>{title}</span>{note}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  href="/pre-registration"
                  className="w-full flex items-center justify-center gap-2 font-bold rounded-xl text-white"
                  style={{ background: "#3b82f6", boxShadow: "0 10px 30px -12px rgba(59,130,246,0.6)", padding: "0.875rem 1.5rem", fontSize: "0.95rem" }}
                >
                  {t("Card.Cta")}
                  <ArrowRightIcon />
                </Link>
                <p style={{ textAlign: "center", fontSize: "0.72rem", color: "#374151", marginTop: "0.875rem" }}>
                  {t("Card.FinePrint", { price: PRICE })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <section style={{ borderTop: "1px solid rgba(75,85,99,0.4)" }}>
        <div style={{ maxWidth: "80rem", margin: "0 auto", padding: "3.5rem 1.5rem" }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px">
          {trustItems.map(([title, desc], i) => (
            <div key={title} className="px-2 lg:px-6 py-2" style={i > 0 ? { borderLeft: "1px solid rgba(55,65,81,0.4)" } : undefined}>
              <p style={{ color: "#fff", fontWeight: 600, fontSize: "0.98rem", marginBottom: "0.375rem" }}>{title}</p>
              <p style={{ color: "#4b5563", fontSize: "0.85rem", lineHeight: 1.6 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ── */}
      <section id="include" style={{ borderTop: "1px solid rgba(75,85,99,0.4)" }}>
        <div style={{ maxWidth: "80rem", margin: "0 auto", padding: "6rem 1.5rem" }}>
          <div style={{ maxWidth: "40rem", marginBottom: "3rem" }}>
            <p style={{ fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600, color: "#60A5FA", marginBottom: "1rem" }}>
              {t("Included.Kicker")}
            </p>
            <h2 style={{ fontSize: "clamp(1.7rem, 3vw, 2.6rem)", fontWeight: 800, lineHeight: 1.12, letterSpacing: "-0.02em", marginBottom: "1rem" }}>
              {t("Included.Heading")}
            </h2>
            <p style={{ color: "#9ca3af", fontSize: "1.02rem", lineHeight: 1.7 }}>
              {t("Included.Description")}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {includedFeatures.map(([title, desc]) => (
              <div
                key={title}
                style={{ background: "#161f2d", border: "1px solid rgba(75,85,99,0.4)", borderRadius: "0.8rem", padding: "1.25rem", transition: "border-color .15s ease, transform .15s ease" }}
                className="hover:border-blue-500/40 hover:-translate-y-0.5"
              >
                <div style={{ width: 18, height: 18, borderRadius: "999px", background: "rgba(59,130,246,0.14)", color: "#60A5FA", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: "0.875rem" }}>
                  <CheckIcon />
                </div>
                <p style={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem", marginBottom: "0.375rem" }}>{title}</p>
                <p style={{ color: "#4b5563", fontSize: "0.83rem", lineHeight: 1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CALCULATOR ── */}
      <section style={{ borderTop: "1px solid rgba(75,85,99,0.4)", background: "#0c121d" }}>
        <div style={{ maxWidth: "80rem", margin: "0 auto", padding: "6rem 1.5rem" }}>
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
            {/* Left copy */}
            <div className="lg:sticky lg:top-24">
              <p style={{ fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600, color: "#34D399", marginBottom: "1rem" }}>
                {t("Calculator.Kicker")}
              </p>
              <h2 style={{ fontSize: "clamp(1.7rem, 3vw, 2.6rem)", fontWeight: 800, lineHeight: 1.12, letterSpacing: "-0.02em", marginBottom: "1.25rem" }}>
                {t("Calculator.Heading")}
              </h2>
              <p style={{ color: "#9ca3af", fontSize: "1.02rem", lineHeight: 1.7, marginBottom: "1.5rem" }}>
                {t("Calculator.DescriptionPre")}{" "}
                <em style={{ color: "#e5e7eb", fontStyle: "normal", fontWeight: 500 }}>{t("Calculator.DescriptionEvery")}</em>{" "}
                {t("Calculator.DescriptionPost")}
              </p>
              <div style={{ background: "#151d2a", border: "1px solid rgba(75,85,99,0.55)", borderRadius: "1rem", padding: "1.25rem" }}>
                <div className="flex items-center gap-3">
                  <span style={{ width: 18, height: 18, borderRadius: "999px", background: "rgba(59,130,246,0.14)", color: "#60A5FA", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <DollarIcon />
                  </span>
                  <p style={{ fontSize: "0.86rem", color: "#d1d5db" }}>
                    {t("Calculator.CardNote")}{" "}
                    <span style={{ color: "#fff", fontWeight: 600 }}>{t("Calculator.CardNotePrice", { price: PRICE })}</span>.
                  </p>
                </div>
              </div>
            </div>

            {/* Calc card */}
            <div style={{ background: "#1f2937", border: "1px solid rgba(75,85,99,0.55)", borderRadius: "1rem", padding: "2.25rem" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "1.75rem" }}>
                {[
                  { label: t("Calculator.BookingsLabel"), value: bookings, setValue: setBookings, min: 50, max: 1500, step: 10, display: fmt(bookings) },
                  { label: t("Calculator.ValueLabel"), value: avgValue, setValue: setAvgValue, min: 30, max: 300, step: 5, display: `${fmt(avgValue)} RON` },
                  { label: t("Calculator.CommissionLabel"), value: commission, setValue: setCommission, min: 5, max: 30, step: 1, display: `${commission}%` },
                ].map(({ label, value, setValue, min, max, step, display }) => (
                  <div key={label}>
                    <div className="flex justify-between items-baseline mb-3">
                      <label style={{ fontSize: "0.9rem", color: "#d1d5db", fontWeight: 500 }}>{label}</label>
                      <span style={{ color: "#fff", fontWeight: 700, fontSize: "1.125rem" }}>{display}</span>
                    </div>
                    <input
                      type="range"
                      min={min}
                      max={max}
                      step={step}
                      value={value}
                      onChange={e => setValue(Number(e.target.value))}
                      style={{ WebkitAppearance: "none", appearance: "none", width: "100%", height: 6, borderRadius: "999px", background: "rgba(255,255,255,0.1)", outline: "none", cursor: "pointer" }}
                    />
                  </div>
                ))}
              </div>

              <div style={{ borderTop: "1px solid rgba(75,85,99,0.4)", margin: "2rem 0" }} />

              {/* Bars */}
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1.75rem" }}>
                <div>
                  <div className="flex justify-between text-[0.8rem] mb-1.5">
                    <span style={{ color: "#9ca3af" }}>{t("Calculator.MarketplaceLabel")}</span>
                    <span style={{ color: "#d1d5db", fontWeight: 600 }}>{fmt(mpCost)} RON</span>
                  </div>
                  <div style={{ width: "100%", height: 30, borderRadius: "0.5rem", background: "rgba(244,63,94,0.22)", display: "flex", alignItems: "center", padding: "0 0.7rem", overflow: "hidden" }}>
                    <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#fda4af", width: `${mpBarPct}%`, whiteSpace: "nowrap" }}>
                      {t("Calculator.MarketplaceTag")}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-[0.8rem] mb-1.5">
                    <span style={{ color: "#9ca3af" }}>{t("Calculator.AtlivoLabel")}</span>
                    <span style={{ color: "#d1d5db", fontWeight: 600 }}>{PRICE} RON</span>
                  </div>
                  <div style={{ width: "100%", height: 30, borderRadius: "0.5rem", background: "rgba(59,130,246,0.18)", overflow: "hidden" }}>
                    <div style={{ width: `${atBarPct}%`, height: "100%", display: "flex", alignItems: "center", padding: "0 0.7rem", minWidth: "5rem" }}>
                      <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#60A5FA", whiteSpace: "nowrap" }}>
                        {t("Calculator.AtlivoTag")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Savings */}
              <div style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.25)", borderRadius: "0.75rem", padding: "1.25rem", textAlign: "center" }}>
                <p style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: "#34D399", marginBottom: "0.375rem" }}>
                  {t("Calculator.SavingsKicker")}
                </p>
                <p style={{ fontWeight: 800, letterSpacing: "-0.02em", color: "#34D399", fontSize: "clamp(2rem,4vw,2.7rem)" }}>
                  {fmt(savings)} RON<span style={{ color: "#4b5563", fontSize: "1rem", fontWeight: 600 }}>{t("Calculator.SavingsPerMonth")}</span>
                </p>
                <p style={{ color: "#9ca3af", fontSize: "0.88rem", marginTop: "0.375rem" }}>
                  {t("Calculator.SavingsYear", { annual: fmt(savings * 12) })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ borderTop: "1px solid rgba(75,85,99,0.4)", background: "#0c121d" }}>
        <div style={{ maxWidth: "48rem", margin: "0 auto", padding: "7rem 1.5rem", textAlign: "center" }}>
          <p style={{ fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600, color: "#60A5FA", marginBottom: "1.25rem" }}>
            {t("FinalCta.Kicker")}
          </p>
          <h2 style={{ fontSize: "clamp(1.7rem, 3vw, 2.6rem)", fontWeight: 800, lineHeight: 1.12, letterSpacing: "-0.02em", marginBottom: "1.25rem" }}>
            {t("FinalCta.HeadingPre")} <span style={{ color: "#60A5FA" }}>{t("FinalCta.HeadingAccent")}</span>{t("FinalCta.HeadingPost")}
          </h2>
          <p style={{ color: "#9ca3af", fontSize: "1.05rem", lineHeight: 1.7, maxWidth: "36rem", margin: "0 auto 2.25rem" }}>
            {t("FinalCta.DescriptionPre")}{" "}
            <span style={{ color: "#fff", fontWeight: 600 }}>{t("FinalCta.DescriptionPrice", { price: PRICE })}</span>
            {t("FinalCta.DescriptionPost")}
          </p>
          <Link
            href="/pre-registration"
            className="inline-flex items-center gap-2 font-bold rounded-xl text-white"
            style={{ background: "#3b82f6", boxShadow: "0 10px 30px -12px rgba(59,130,246,0.6)", padding: "1rem 2.25rem", fontSize: "1rem" }}
          >
            {t("FinalCta.Cta")}
            <ArrowRightIcon />
          </Link>
          <p style={{ fontSize: "0.8rem", color: "#4b5563", marginTop: "1rem" }}>{t("FinalCta.FinePrint")}</p>
        </div>
      </section>
    </div>
  );
}
